import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { toast } from 'sonner';
import { 
  MessageSquare, Send, Loader2, Phone, User, Clock, 
  CheckCircle2, XCircle, AlertCircle, FileText, RefreshCw,
  ChevronRight, ArrowDownLeft, ArrowUpRight, Settings
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SMSComposer = ({ 
  caseId, 
  contactId, 
  contactName, 
  contactPhone,
  onMessageSent 
}) => {
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplates, setShowTemplates] = useState(false);
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [settings, setSettings] = useState(null);
  const [phoneInput, setPhoneInput] = useState(contactPhone || '');

  const getToken = () => localStorage.getItem('token');

  // Fetch SMS settings
  const fetchSettings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sms/settings`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSettings(data);
      }
    } catch (error) {
      console.error('Error fetching SMS settings:', error);
    }
  }, []);

  // Fetch templates
  const fetchTemplates = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/sms/templates`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setTemplates(data.templates || []);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  }, []);

  // Fetch message history
  const fetchHistory = useCallback(async () => {
    if (!caseId && !contactPhone) return;
    
    setLoadingHistory(true);
    try {
      let url = `${API_URL}/api/sms/messages?`;
      if (caseId) url += `case_id=${caseId}&`;
      if (contactPhone) url += `contact_phone=${encodeURIComponent(contactPhone)}&`;
      url += 'limit=20';

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHistory(data.messages || []);
      }
    } catch (error) {
      console.error('Error fetching SMS history:', error);
    } finally {
      setLoadingHistory(false);
    }
  }, [caseId, contactPhone]);

  useEffect(() => {
    fetchSettings();
    fetchTemplates();
    fetchHistory();
  }, [fetchSettings, fetchTemplates, fetchHistory]);

  useEffect(() => {
    setPhoneInput(contactPhone || '');
  }, [contactPhone]);

  const handleSend = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    if (!phoneInput.trim()) {
      toast.error('Please enter a phone number');
      return;
    }

    setSending(true);
    try {
      const response = await fetch(`${API_URL}/api/sms/send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          case_id: caseId,
          contact_id: contactId,
          contact_name: contactName,
          phone_number: phoneInput,
          message: message.trim(),
          template_id: selectedTemplate?.id
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        toast.success(data.demo_mode ? 'SMS queued (demo mode)' : 'SMS sent successfully');
        setMessage('');
        setSelectedTemplate(null);
        fetchHistory();
        if (onMessageSent) onMessageSent(data.sms);
      } else {
        toast.error(data.message || 'Failed to send SMS');
      }
    } catch (error) {
      toast.error('Failed to send SMS');
    } finally {
      setSending(false);
    }
  };

  const applyTemplate = (template) => {
    setMessage(template.content);
    setSelectedTemplate(template);
    setShowTemplates(false);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered':
        return <CheckCircle2 className="w-3 h-3 text-green-500" />;
      case 'sent':
        return <CheckCircle2 className="w-3 h-3 text-blue-500" />;
      case 'failed':
        return <XCircle className="w-3 h-3 text-red-500" />;
      case 'queued':
        return <Clock className="w-3 h-3 text-amber-500" />;
      default:
        return <Clock className="w-3 h-3 text-gray-400" />;
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'Just now';
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  const charCount = message.length;
  const maxChars = 1600;
  const segmentCount = Math.ceil(charCount / 160) || 1;

  return (
    <Card className="border-blue-200">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5 text-blue-600" />
            <CardTitle className="text-base">SMS Messaging</CardTitle>
          </div>
          {settings && !settings.twilio_configured && (
            <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50">
              <AlertCircle className="w-3 h-3 mr-1" />
              Demo Mode
            </Badge>
          )}
        </div>
        <CardDescription>
          Send text messages to {contactName || 'contact'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Phone Input */}
        <div className="space-y-2">
          <Label className="flex items-center gap-2">
            <Phone className="w-4 h-4" />
            Phone Number
          </Label>
          <Input
            value={phoneInput}
            onChange={(e) => setPhoneInput(e.target.value)}
            placeholder="+1 (555) 123-4567"
            data-testid="sms-phone-input"
          />
        </div>

        {/* Message Composer */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Message</Label>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setShowTemplates(true)}
              className="text-xs"
            >
              <FileText className="w-3 h-3 mr-1" />
              Use Template
            </Button>
          </div>
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type your message..."
            rows={4}
            maxLength={maxChars}
            data-testid="sms-message-input"
          />
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>
              {charCount}/{maxChars} characters
              {segmentCount > 1 && ` (${segmentCount} segments)`}
            </span>
            {selectedTemplate && (
              <Badge variant="secondary" className="text-xs">
                Template: {selectedTemplate.name}
              </Badge>
            )}
          </div>
        </div>

        {/* Send Button */}
        <Button
          onClick={handleSend}
          disabled={sending || !message.trim() || !phoneInput.trim()}
          className="w-full bg-blue-600 hover:bg-blue-700"
          data-testid="sms-send-btn"
        >
          {sending ? (
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
          ) : (
            <Send className="w-4 h-4 mr-2" />
          )}
          Send SMS
        </Button>

        {/* Message History */}
        {(history.length > 0 || loadingHistory) && (
          <div className="border-t pt-4 mt-4">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-medium flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Recent Messages
              </Label>
              <Button
                variant="ghost"
                size="sm"
                onClick={fetchHistory}
                disabled={loadingHistory}
              >
                <RefreshCw className={`w-3 h-3 ${loadingHistory ? 'animate-spin' : ''}`} />
              </Button>
            </div>
            
            {loadingHistory ? (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="w-4 h-4 animate-spin mr-2" />
                <span className="text-sm text-muted-foreground">Loading...</span>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {history.map((msg) => (
                  <div
                    key={msg.id}
                    className={`p-2 rounded-lg text-sm ${
                      msg.direction === 'outbound' 
                        ? 'bg-blue-50 ml-4' 
                        : 'bg-gray-100 mr-4'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-1">
                      {msg.direction === 'outbound' ? (
                        <ArrowUpRight className="w-3 h-3 text-blue-500" />
                      ) : (
                        <ArrowDownLeft className="w-3 h-3 text-green-500" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {msg.direction === 'outbound' ? msg.sent_by_name || 'You' : msg.contact_name || msg.contact_phone}
                      </span>
                      <span className="text-xs text-muted-foreground ml-auto flex items-center gap-1">
                        {getStatusIcon(msg.status)}
                        {formatTime(msg.created_at)}
                      </span>
                    </div>
                    <p className="text-gray-700">{msg.message}</p>
                    {msg.error_message && (
                      <p className="text-xs text-red-500 mt-1">{msg.error_message}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>

      {/* Templates Dialog */}
      <Dialog open={showTemplates} onOpenChange={setShowTemplates}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              SMS Templates
            </DialogTitle>
            <DialogDescription>
              Select a template to use as your message
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {templates.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">
                No templates available
              </p>
            ) : (
              templates.map((template) => (
                <div
                  key={template.id}
                  className="p-3 border rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => applyTemplate(template)}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-medium text-sm">{template.name}</span>
                    <Badge variant="secondary" className="text-xs">
                      {template.category}
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {template.content}
                  </p>
                  {template.variables?.length > 0 && (
                    <div className="flex gap-1 mt-2 flex-wrap">
                      {template.variables.map((v) => (
                        <code key={v} className="text-xs px-1 py-0.5 bg-slate-200 rounded">
                          {`{{${v}}}`}
                        </code>
                      ))}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTemplates(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default SMSComposer;
